import { uniq } from 'lodash-es'
import { useMemo } from 'react'

import { useFormModelState, validateEmailString } from '@fv/client-core'
import type { AccountUserProfileUpdateDto, Workflow } from '@fv/models'

import { useAppContext } from '@/hooks/settings'
import { useCurrentUser } from '@/hooks/settings/useAppContext'

import { useAccountSettings } from '../settings/account-settings/hooks'

export const preferredWorkflowOrder: Workflow[] = ['ltl', 'truckload', 'parcel']

export type WorkflowWithStatus = {
  workflow: Workflow
  enabled: boolean
}

export function orderWorkflows(
  workflows: Workflow[],
  defaultWorkflow: Workflow = 'ltl',
): Workflow[] {
  return uniq([
    defaultWorkflow,
    ...preferredWorkflowOrder.filter(workflow => workflow !== defaultWorkflow),
  ]).filter(workflow => workflows.includes(workflow))
}

function orderAccountWorkflows(
  workflows: Workflow[],
  allowedWorkflows: Workflow[],
  defaultWorkflow: Workflow,
): WorkflowWithStatus[] {
  return orderWorkflows(workflows, defaultWorkflow).map(workflow => ({
    workflow,
    enabled: allowedWorkflows.includes(workflow),
  }))
}

export function usePreferredWorkflows(): Workflow[] {
  const { workflows } = useCurrentUser()
  const { quoting } = useAccountSettings()
  const defaultWorkflow = quoting.defaultWorkflow ?? 'ltl'

  return useMemo(
    () => orderWorkflows(workflows, defaultWorkflow),
    [workflows, defaultWorkflow],
  )
}

export function useWorkflowsWithStatus(): WorkflowWithStatus[] {
  const { workflows, allowedWorkflows } = useCurrentUser()
  const { quoting } = useAccountSettings()
  const defaultWorkflow = quoting.defaultWorkflow ?? 'ltl'

  return useMemo(
    () => orderAccountWorkflows(workflows, allowedWorkflows, defaultWorkflow),
    [workflows, allowedWorkflows, defaultWorkflow],
  )
}

function getDefaultWorkflow(
  workflowsWithStatus: WorkflowWithStatus[],
): Workflow {
  return (
    workflowsWithStatus.find(w => w.enabled)?.workflow ??
    workflowsWithStatus[0]?.workflow ??
    'ltl'
  )
}

export function useDefaultWorkflow(): Workflow {
  const workflowsWithStatus = useWorkflowsWithStatus()
  return useMemo(
    () => getDefaultWorkflow(workflowsWithStatus),
    [workflowsWithStatus],
  )
}

export const useProfileInfoFormModel = () => {
  const contextQuery = useAppContext()
  const user = contextQuery.data.user
  return useFormModelState<AccountUserProfileUpdateDto>({
    initialValue: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      company: user?.company ?? '',
      title: user?.title ?? '',
      email: user?.email ?? '',
      phoneNumber: user?.phoneNumber?.replace(/\D/g, '') ?? '',
    },
    schema: {
      email: {
        validate: e => (validateEmailString(e) ? '' : 'Must be a valid email'),
      },
    },
  })
}
